import type { LuminaInteraction } from '@atlassian/lumina/interaction';

export type LuminaAnalyticsInteractionType =
	| 'actionCardClicked'
	| 'actionCardContextMenuOpened'
	| 'carouselNext'
	| 'carouselPrevious'
	| 'peopleCollaboratorsExpanded'
	| 'peopleHierarchyNodeClicked'
	| 'peopleMessageStarted'
	| 'peopleProfileTabSelected'
	| 'peopleProfileViewed'
	| 'peopleRecentWorkExpanded'
	| 'peopleRecentWorkItemContextMenuOpened'
	| 'peopleRecentWorkItemOpened'
	| 'peopleRecentWorkViewAllOpened'
	| 'peopleReportingLinesViewed'
	| 'sectionCollapsed'
	| 'sectionExpanded'
	| 'smartLinkClicked'
	| 'smartLinkContextMenuOpened'
	| 'sourceLinkContextMenuOpened'
	| 'sourceLinkCopied'
	| 'sourceLinkOpened';

const ignoreUnexpectedInteraction = (_value: never): undefined => undefined;

export const getLuminaAnalyticsInteractionType = (
	interaction: LuminaInteraction,
): LuminaAnalyticsInteractionType | undefined => {
	switch (interaction.type) {
		case 'sourceActivated':
			switch (interaction.activation) {
				case 'open':
					return 'sourceLinkOpened';
				case 'copy':
					return 'sourceLinkCopied';
				case 'contextMenu':
					return 'sourceLinkContextMenuOpened';
				default:
					return ignoreUnexpectedInteraction(interaction.activation);
			}
		case 'cardActivated':
			const component = interaction.component;
			switch (component) {
				case 'smartLink':
					return 'smartLinkClicked';
				case 'actionCard':
					return 'actionCardClicked';
				default:
					return ignoreUnexpectedInteraction(component);
			}
		case 'cardContextMenuOpened':
			const contextMenuComponent = interaction.component;
			switch (contextMenuComponent) {
				case 'smartLink':
					return 'smartLinkContextMenuOpened';
				case 'actionCard':
					return 'actionCardContextMenuOpened';
				default:
					return ignoreUnexpectedInteraction(contextMenuComponent);
			}
		case 'sectionStateChanged':
			switch (interaction.state) {
				case 'expanded':
					return 'sectionExpanded';
				case 'collapsed':
					return 'sectionCollapsed';
				default:
					return ignoreUnexpectedInteraction(interaction.state);
			}
		case 'carouselNavigated':
			switch (interaction.direction) {
				case 'previous':
					return 'carouselPrevious';
				case 'next':
					return 'carouselNext';
				default:
					return ignoreUnexpectedInteraction(interaction.direction);
			}
		case 'peoplePreviewInteracted':
			switch (interaction.action) {
				case 'viewProfile':
					return 'peopleProfileViewed';
				case 'viewReportingLines':
					return 'peopleReportingLinesViewed';
				case 'startMessage':
					return 'peopleMessageStarted';
				case 'openRecentWorkItem':
					return 'peopleRecentWorkItemOpened';
				case 'openRecentWorkItemContextMenu':
					return 'peopleRecentWorkItemContextMenuOpened';
				case 'viewAllRecentWork':
					return 'peopleRecentWorkViewAllOpened';
				case 'expandRecentWork':
					return 'peopleRecentWorkExpanded';
				case 'expandCollaborators':
					return 'peopleCollaboratorsExpanded';
				case 'selectTab':
					return 'peopleProfileTabSelected';
				case 'clickHierarchyNode':
					return 'peopleHierarchyNodeClicked';
				default:
					return ignoreUnexpectedInteraction(interaction.action);
			}
		default:
			return ignoreUnexpectedInteraction(interaction);
	}
};
